import { addSlashes, withCompanyPrefix } from "../route.utils";

/**
 * Resolved URLs for the per-module settings pages that were moved out of the
 * company-wide Company Settings page.
 *
 * WHY ITS OWN MODULE: `routes.settings.ts` needs these to serve the legacy
 * `/company/settings/<x>` redirects, and each module's route file needs them for
 * its `COMPANY_*_PATHS` object. If `routes.settings.ts` imported the module route
 * files directly it would pull their eagerly-imported page components in at
 * config-eval time, reordering module evaluation and detonating latent import
 * cycles (that is exactly how `routes.offboarding.ts` ⇄
 * `payrolls/final-settlements/detail` started throwing a TDZ
 * ReferenceError). This file imports nothing but `route.utils`, so it can never
 * be part of a cycle.
 *
 * The `?vTab=` VALUES are the public deep-link contract carried over from the old
 * Company Settings pages — never rename one.
 */

/**
 * Path segment every module uses for its settings page. Each module route file
 * repeats this as a literal in its own PATH_SEGMENTS enum — `isolatedModules`
 * forbids an enum member initialized from an imported const — so keep them in
 * sync if this ever changes.
 */
const MODULE_SETTINGS_SEGMENT = "settings";

const moduleRoot = (moduleSegment: string) =>
  withCompanyPrefix(addSlashes(moduleSegment));

const settingsRoot = (moduleSegment: string) =>
  withCompanyPrefix(addSlashes(moduleSegment, MODULE_SETTINGS_SEGMENT));

const vTab = (root: string, value: string) => `${root}?vTab=${value}`;

/**
 * Deep link straight into one branch's configuration, optionally onto a single
 * sub-tab: `…?vTab=branch-payroll-configure&branch=5&sTab=wps`.
 *
 * `branch` is the `DETAIL_PARAM.branch` the branch list reads, and `sTab` is
 * the rail param the branch detail reads. Both are part of the same public
 * deep-link contract as the `?vTab=` values, so callers pass the literal
 * `sTab` value (`"wps"`, `"payment-method"`, `"payroll-groups"`, …) — see the
 * `TABS` array in `branch-payroll-configure/_components/BranchDetail.tsx`.
 *
 * Exists so screens outside Payroll Settings — the run wizard's readiness
 * notice, for one — can send a user to the exact setting that is missing
 * instead of dropping them at the settings root to hunt for it.
 */
const branchTab = (root: string, branchId: number, sTab?: string) =>
  `${vTab(root, "branch-payroll-configure")}&branch=${branchId}` +
  (sTab ? `&sTab=${sTab}` : "");

// ── Teams ────────────────────────────────────────────────────────────────────
const TEAMS = settingsRoot("teams");
/** Segment for the team-role upsert screens nested under Teams settings. */
const TEAM_ROLES_SEGMENT = "team-roles";
export const DYNAMIC_TEAM_ROLE_ID_KEY = "teamRoleId";

// ── Time & Attendance (also owns the former Shift Settings tab and the former
// standalone Overtime Policy page) ──────────────────────────────────────────
const TIME_ATTENDANCE = settingsRoot("time-attendance");

// ── Payroll (also owns the former Compensation Settings tab) ─────────────────
const PAYROLL = settingsRoot("payroll");

// ── Leave Management ─────────────────────────────────────────────────────────
const LEAVE_MANAGEMENT = settingsRoot("leave-management");

// ── Assets / Expenses / Documents / Performance / Onboarding ─────────────────
const ASSETS = settingsRoot("assets");
const EXPENSES = settingsRoot("expenses");
const SALES_COMMISSIONS = settingsRoot("sales-commissions");
const DOCUMENTS = settingsRoot("documents");
const PERFORMANCE = settingsRoot("performance");
const ONBOARDING = settingsRoot("onboarding");

// ── Recruitment ───────────────────────────────────────────────────────────────
const RECRUITMENT = settingsRoot("recruitment");

// ── Digital ID ───────────────────────────────────────────────────────────────
const DIGITAL_ID = settingsRoot("digital-id");

// ── Biometric Devices ────────────────────────────────────────────────────────
const BIOMETRIC_DEVICES = settingsRoot("biometric-devices");

/** Segment for the metric form screens nested under Performance settings. */
const PERFORMANCE_METRICS_SEGMENT = "metrics";

export const MODULE_SETTINGS_PATHS = Object.freeze({
  TEAMS: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("teams"),
    ROOT: TEAMS,
    VTAB_ROLES_RESPS: vTab(TEAMS, "roles-and-resps"),
    TEAM_ROLES: `${TEAMS}/${TEAM_ROLES_SEGMENT}`,
    TEAM_ROLE_CREATE: `${TEAMS}/${TEAM_ROLES_SEGMENT}/create`,
    TEAM_ROLE_VIEW: (id: string | number) =>
      `${TEAMS}/${TEAM_ROLES_SEGMENT}/${id}`,
    TEAM_ROLE_EDIT: (id: string | number) =>
      `${TEAMS}/${TEAM_ROLES_SEGMENT}/${id}/edit`,
  }),

  TIME_ATTENDANCE: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("time-attendance"),
    ROOT: TIME_ATTENDANCE,
    VTAB_QUICK_ACTIVITY: vTab(TIME_ATTENDANCE, "quick-activity"),
    VTAB_REMINDERS: vTab(TIME_ATTENDANCE, "reminders"),
    VTAB_RULES: vTab(TIME_ATTENDANCE, "time-attendance-rules"),
    VTAB_DEFAULT_RULE: vTab(TIME_ATTENDANCE, "time-attendance-default-rule"),
    VTAB_SHIFTS: vTab(TIME_ATTENDANCE, "shifts"),
    VTAB_OVERTIME: vTab(TIME_ATTENDANCE, "overtime-policy"),
  }),

  PAYROLL: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("payroll"),
    ROOT: PAYROLL,
    VTAB_OVERVIEW: vTab(PAYROLL, "overview"),
    VTAB_POLICIES: vTab(PAYROLL, "payroll-policies"),
    VTAB_COUNTRY_RULES: vTab(PAYROLL, "country-rules"),
    VTAB_BRANCH: vTab(PAYROLL, "branch-payroll-configure"),
    /** One branch's config, optionally on a given sub-tab. See `branchTab`. */
    BRANCH_TAB: (branchId: number, sTab?: string) =>
      branchTab(PAYROLL, branchId, sTab),
    VTAB_PAY_TYPES: vTab(PAYROLL, "pay-types"),
    VTAB_PAY: vTab(PAYROLL, "pay"),
    VTAB_DEDUCTIONS: vTab(PAYROLL, "deductions"),
    VTAB_BENEFITS: vTab(PAYROLL, "benefits"),
  }),

  LEAVE_MANAGEMENT: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("leave-management"),
    ROOT: LEAVE_MANAGEMENT,
    VTAB_GROUP: vTab(LEAVE_MANAGEMENT, "leave-group"),
    VTAB_TYPE: vTab(LEAVE_MANAGEMENT, "leave-type"),
    VTAB_POLICY: vTab(LEAVE_MANAGEMENT, "leave-policy"),
    VTAB_BLACKOUT: vTab(LEAVE_MANAGEMENT, "blackout-periods"),
  }),

  ASSETS: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("assets"),
    ROOT: ASSETS,
    VTAB_ASSET_TYPES: vTab(ASSETS, "asset-types"),
    VTAB_ASSET_BRANDS: vTab(ASSETS, "asset-brands"),
  }),

  EXPENSES: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("expenses"),
    ROOT: EXPENSES,
    VTAB_CATEGORIES: vTab(EXPENSES, "categories"),
  }),

  SALES_COMMISSIONS: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("sales-commissions"),
    ROOT: SALES_COMMISSIONS,
    VTAB_PLANS: vTab(SALES_COMMISSIONS, "commission-plans"),
  }),

  DOCUMENTS: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("documents"),
    ROOT: DOCUMENTS,
    VTAB_DOC_TYPES: vTab(DOCUMENTS, "document-types"),
  }),

  PERFORMANCE: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("performance"),
    ROOT: PERFORMANCE,
    VTAB_METRICS: vTab(PERFORMANCE, "metrics"),
    METRICS_CREATE: `${PERFORMANCE}/${PERFORMANCE_METRICS_SEGMENT}/create`,
    METRICS_EDIT: (metricId: string | number) =>
      `${PERFORMANCE}/${PERFORMANCE_METRICS_SEGMENT}/${metricId}/edit`,
  }),

  ONBOARDING: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("onboarding"),
    ROOT: ONBOARDING,
    VTAB_DIGITAL_SYSTEMS: vTab(ONBOARDING, "digital-systems"),
  }),

  RECRUITMENT: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("recruitment"),
    ROOT: RECRUITMENT,
    VTAB_SCREENING: vTab(RECRUITMENT, "screening"),
    VTAB_MATCH_WEIGHTING: vTab(RECRUITMENT, "match-weighting"),
    VTAB_INTERVIEWS: vTab(RECRUITMENT, "interviews"),
    VTAB_JOB_LIBRARY: vTab(RECRUITMENT, "job-library"),
    VTAB_EMAIL_TEMPLATES: vTab(RECRUITMENT, "email-templates"),
    VTAB_OFFER_LETTERS: vTab(RECRUITMENT, "offer-letters"),
  }),

  DIGITAL_ID: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("digital-id"),
    ROOT: DIGITAL_ID,
    // Value carried over unchanged from the old Company Settings tab's vTab.
    VTAB_BRAND_LOCK: vTab(DIGITAL_ID, "brand-lock"),
  }),

  BIOMETRIC_DEVICES: Object.freeze({
    /** The module's own root path — `parentRoot` for search groups. */
    MODULE_ROOT: moduleRoot("biometric-devices"),
    ROOT: BIOMETRIC_DEVICES,
    VTAB_AGENTS: vTab(BIOMETRIC_DEVICES, "agents"),
    VTAB_DIRECT_TERMINALS: vTab(BIOMETRIC_DEVICES, "direct-terminals"),
  }),
});
